// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import Ajv from "ajv";

import { split } from "@transformers/schema";
import { removeNullAndUndefinedProps } from "@transformers/object";
import actionCreator from "@modules/service/actionCreator";
import * as selectors from "@modules/service/selectors";
import { View, Col, Button, ButtonGroup, Icon } from "@components";
import type { StepProps } from "@components";
import type { Rtw, RtwAbsenceReason } from "@modules/service/reducer/rtw/types";
import { RtwSettings, RtwReasonScope } from "@modules/service/components";

const REASON_SCOPE = {
  ALL_REASONS: "ALL_REASONS",
  SPECIFIC_REASONS: "SPECIFIC_REASONS",
};

type StepData = $Exact<{
  settings: ?Object,
  reasonScope: ?string,
  absenceReasons: ?Array<RtwAbsenceReason>,
}>;

type OwnProps = $Exact<{
  editing: boolean,
}>;

type PropsFromState = $Exact<{
  schema: ?Object,
  draft: ?Rtw,
}>;

type PropsFromDispatch = $Exact<{
  setDraft: typeof actionCreator.SetRtwDraft,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...StepProps,
  ...PropsFromLocalize,
}>;

export class RTWStepSettings extends React.Component<Props> {
  form: *;
  validateSettings: *;
  validateReasonScope: *;
  validateAbsenceReasons: *;

  componentDidMount() {
    if (this.props.schema && this.props.draft) {
      this.setValidators();
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (
      this.props.schema &&
      this.props.draft &&
      (this.props.schema !== prevProps.schema || this.props.draft !== prevProps.draft)
    ) {
      this.setValidators();
    }
  }

  setValidators = () => {
    this.validateSettings = new Ajv().compile(this.schema.settings || {});
    this.validateReasonScope = new Ajv().compile(this.schema.reasonScope || {});
    this.validateAbsenceReasons = new Ajv().compile(this.schema.absenceReasons || {});
  };

  get data(): StepData {
    const draft = this.props.draft?.content || {};
    return {
      settings: {
        id: (this.props.draft || {}).id || null,
        ...(draft.name ? { name: draft.name } : {}),
        ...(draft.creationRule !== undefined ? { creationRule: draft.creationRule } : {}),
        ...(draft.absenceTypeScope !== undefined
          ? { absenceTypeScope: draft.absenceTypeScope }
          : {}),
        ...(draft.eligibilityRule !== undefined ? { eligibilityRule: draft.eligibilityRule } : {}),
        ...(draft.isActive !== undefined ? { isActive: draft.isActive } : {}),
        ...(draft.managerCanChangeReason !== undefined
          ? { managerCanChangeReason: draft.managerCanChangeReason }
          : {}),
        ...(draft.lengthScope !== undefined ? { lengthScope: draft.lengthScope } : {}),
      },
      ...(draft.reasonScope ? { reasonScope: draft.reasonScope } : {}),
      absenceReasons: draft.absenceReasons || null,
    };
  }

  get schema() {
    const absenceTypeScope = (this.props.draft?.content || {}).absenceTypeScope;

    let dependentSchema = null;
    if (this.props.schema && absenceTypeScope) {
      dependentSchema = this.props.schema.dependencies.absenceTypeScope.oneOf.find(
        o => (o.properties.absenceTypeScope || {}).const === absenceTypeScope,
      );
    }

    let absenceReasonWithMinItems = null;
    if (dependentSchema) {
      absenceReasonWithMinItems = {
        ...dependentSchema.properties.absenceReasons,
        minItems: 1,
        uniqueItems: true,
      };
    }

    let absenceReasonsItem = null;
    if (absenceReasonWithMinItems) {
      absenceReasonsItem = absenceReasonWithMinItems.items;
    }

    return {
      settings: split(this.props.schema, [
        "id",
        "name",
        "creationRule",
        "absenceTypeScope",
        "eligibilityRule",
        "isActive",
        "managerCanChangeReason",
        "lengthScope",
      ]),
      reasonScope: split(this.props.schema, ["reasonScope"]),
      absenceReasons: absenceReasonWithMinItems,
      absenceReasonsItem,
    };
  }

  isValid = (data: StepData) => {
    if (!this.validateSettings || !this.validateReasonScope || !this.validateAbsenceReasons) {
      return false;
    }

    const isValid =
      this.validateSettings(removeNullAndUndefinedProps(data.settings || {})) &&
      this.validateReasonScope({ reasonScope: data.reasonScope }) &&
      ((this.validateAbsenceReasonsIds(data.absenceReasons || []) &&
        data.absenceReasons &&
        data.absenceReasons.length > 0) ||
        data.reasonScope === REASON_SCOPE.ALL_REASONS);

    if (process.env.NODE_ENV !== "production" && !isValid) {
      console.warn(
        "isValid errors",
        this.validateSettings.errors,
        this.validateReasonScope.errors,
        this.validateAbsenceReasons.errors,
      );
    }

    return isValid;
  };

  validateAbsenceReasonsIds = (reasons?: Array<RtwAbsenceReason>) => {
    const allowedReasonIds = (
      ((((this.schema.absenceReasons || {}).items || {}).properties || {}).id || {}).oneOf || []
    ).map(item => item.const);
    let valid = true;

    if (reasons) {
      const reasonIds = reasons.map(item => item.id);

      reasonIds.map(reasonId => {
        if (!allowedReasonIds.includes(reasonId)) {
          valid = false;
        }
      });
    }
    return valid;
  };

  handleFormRef = (ref: *) => {
    this.form = ref;
  };

  submitForm = () => {
    this.submitSettings({ formData: this.data.settings });
  };

  submitSettings = (formData: Object) => {
    if (this.props.handleSubmit) {
      this.props.handleSubmit({}, true);
    }
  };

  updateSettings = (formData: Object) => {
    this.props.setDraft({
      ...this.props.draft,
      content: {
        ...this.props.draft?.content,
        ...formData,
        absenceReasons: this.props.draft?.content?.absenceReasons || null,
      },
    });
  };

  updateReasonScope = ({
    reasonScope,
    absenceReasons,
  }: {
    reasonScope: string,
    absenceReasons: ?Array<Object>,
  }) => {
    this.props.setDraft({
      ...this.props.draft,
      content: {
        ...this.props.draft?.content,
        reasonScope,
        absenceReasons: reasonScope === REASON_SCOPE.ALL_REASONS ? null : absenceReasons,
      },
    });
  };

  updateAbsenceReasons = ({ absenceReasons }: { absenceReasons: Array<Object> }) => {
    this.props.setDraft({
      ...this.props.draft,
      content: {
        ...this.props.draft?.content,
        absenceReasons: absenceReasons && absenceReasons.length ? absenceReasons : null,
      },
    });
  };

  getButtonState = (filter: string) => {
    return this.data.reasonScope === filter ? "selected" : null;
  };

  changeAbsenceReasonsFilter = (filter: string) => () => {
    this.updateReasonScope({
      reasonScope: filter,
      absenceReasons: filter === REASON_SCOPE.ALL_REASONS ? null : this.data.absenceReasons,
    });
  };

  render() {
    if (!this.props.editable) {
      return <div />;
    }

    return (
      <View gap={6} width="66%" className="align-self-center">
        {this.schema && this.data && (
          <RtwSettings
            onRef={this.handleFormRef}
            data={this.data.settings}
            schema={this.schema.settings}
            onChange={this.updateSettings}
            onSubmit={this.submitSettings}
            editing={this.props.editing}
          />
        )}
        <View>
          <h6>
            {this.props.t("RETURN_TO_WORK_DETAILS_REASON_SCOPE")}
            <Icon icon="asterisk" size="xs" className="text-warning ml-2" />
          </h6>
          <ButtonGroup>
            <Button
              size="small"
              color={this.getButtonState(REASON_SCOPE.ALL_REASONS)}
              onClick={this.changeAbsenceReasonsFilter(REASON_SCOPE.ALL_REASONS)}
            >
              {this.props.t(REASON_SCOPE.ALL_REASONS)}
            </Button>
            <Button
              size="small"
              color={this.getButtonState(REASON_SCOPE.SPECIFIC_REASONS)}
              onClick={this.changeAbsenceReasonsFilter(REASON_SCOPE.SPECIFIC_REASONS)}
            >
              {this.props.t(REASON_SCOPE.SPECIFIC_REASONS)}
            </Button>
          </ButtonGroup>
          {this.data && this.data.reasonScope === REASON_SCOPE.SPECIFIC_REASONS && (
            <RtwReasonScope
              data={this.data.absenceReasons}
              disabledReasonAdding={!(this.props.draft?.content || {}).absenceTypeScope}
              schema={this.schema.absenceReasonsItem}
              onChange={this.updateAbsenceReasons}
              isEdit
            />
          )}
        </View>

        {this.props.editing ? null : (
          <View direction="row" justifyContent="end">
            <Button onClick={this.submitForm} disabled={!this.isValid(this.data)}>
              {this.props.t("RETURN_TO_WORK_STEP1_NEXT_BUTTON")}
            </Button>
          </View>
        )}
      </View>
    );
  }
}

const mapStateToProps = (state, ownProps): PropsFromState => {
  return {
    schema: selectors.getRtwSchema(state),
    draft: selectors.getRtwDraft(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  setDraft: actionCreator.SetRtwDraft,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(RTWStepSettings);
